import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';

import { auth } from '../firebase/firebase';
import { useAuth } from './AuthProvider';

const spaces = [
  {
    id: 'parent',
    icon: '👨‍👩‍👧‍👦',
    title: 'Espace Parent',
    description: 'Consulter les devoirs, annonces, informations et suivi de votre enfant.',
    accent: 'blue'
  },
  {
    id: 'teacher',
    icon: '👩‍🏫',
    title: 'Espace Enseignant',
    description: 'Accéder aux devoirs, classes, élèves et services pédagogiques.',
    accent: 'orange'
  },
  {
    id: 'administration',
    icon: '🛡️',
    title: 'Administration',
    description: 'Gérer les comptes, réclamations, annonces, devoirs et emplois du temps.',
    accent: 'dark'
  }
];

function loginIdentifierToEmail(identifier, accountType) {
  const value = identifier.trim().toLowerCase();
  if (value.includes('@')) return value;

  const cleanIdentifier = value.replace(/[^a-z0-9._-]/g, '');
  if (accountType === 'parent') return `${cleanIdentifier}@parents.le-defi.local`;
  if (accountType === 'teacher') return `${cleanIdentifier}@teachers.le-defi.local`;
  return `${cleanIdentifier}@le-defi.local`;
}

function accountHome(profile) {
  const raw = String(profile?.accountType || profile?.role || '').trim().toLowerCase();
  if (raw === 'parent') return '/parent';
  if (raw === 'teacher' || raw === 'enseignant') return '/enseignant';
  return '/dashboard';
}

export default function LoginPage() {
  const { user, profile, loading, authError } = useAuth();
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={accountHome(profile)} replace />;
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await signInWithEmailAndPassword(
        auth,
        loginIdentifierToEmail(identifier, selectedSpace.id),
        password
      );
    } catch (err) {
      console.error(err);
      setError('Identifiant ou mot de passe incorrect.');
    } finally {
      setSubmitting(false);
    }
  }

  if (selectedSpace) {
    return (
      <main className={`portal-screen portal-screen-${selectedSpace.id}`}>
        <section className={`portal-login-card portal-login-card-${selectedSpace.id}`}>
          <button
            type="button"
            className="portal-back-button"
            onClick={() => {
              setSelectedSpace(null);
              setError('');
            }}
          >
            ← Retour aux espaces
          </button>

          <img className="portal-logo" src="/logo.png" alt="Groupe Scolaire Le Défi" />
          <div className={`portal-login-icon ${selectedSpace.accent}`}>
            {selectedSpace.icon}
          </div>
          <h1>{selectedSpace.title}</h1>
          <p className="portal-subtitle">Connectez-vous pour accéder à votre espace.</p>

          <form onSubmit={submit} className="portal-login-form">
            <label>
              Email ou nom d’utilisateur
              <input
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
                autoComplete="username"
                placeholder="Votre identifiant"
              />
            </label>

            <label>
              Mot de passe
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
                placeholder="Votre mot de passe"
              />
            </label>

            {(error || authError) && (
              <div className="portal-alert">{error || authError}</div>
            )}

            <button className="portal-primary-button" disabled={submitting}>
              {submitting ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="portal-screen">
      <section className="portal-card">
        <Link to="/" className="portal-site-link">← Retour au site</Link>

        <div className="portal-heading">
          <img className="portal-logo" src="/logo.png" alt="Groupe Scolaire Le Défi" />
          <h1>Choisissez votre espace</h1>
          <p>Connectez-vous selon votre profil pour accéder aux services de l’école.</p>
        </div>

        <div className="portal-registration-callout portal-registration-mobile">
          <strong>Pas encore d’accès à la plateforme ?</strong>
          <Link to="/demande-inscription">Demander une inscription →</Link>
        </div>

        <div className="portal-space-grid">
          {spaces.map((space) => (
            <article className="portal-space-card" key={space.id}>
              <div className={`portal-space-icon ${space.accent}`}>{space.icon}</div>
              <div className="portal-space-copy">
                <h2 className={space.accent}>{space.title}</h2>
                <p>{space.description}</p>
              </div>
              <button
                type="button"
                className="portal-continue-button"
                onClick={() => setSelectedSpace(space)}
              >
                Continuer →
              </button>
            </article>
          ))}
        </div>

        <div className="portal-registration-callout portal-registration-desktop">
          <strong>Vous n’avez pas encore d’accès à la plateforme ?</strong>
          <Link to="/demande-inscription">Demander une inscription →</Link>
        </div>
      </section>
    </main>
  );
}
